import { AuditService } from '../content/audit';
import type { MaterialId } from '../content/materials';
import type { SimEvent } from './events';
import { releaseMachinePackets } from './machine-packets';
import type { PacketMaterial } from './packet';
import type { SimState } from './state';
import type { PacketId } from './types';

/** `sequence` is a record of what this subscription drew, never a plan for what the next one will:
 * clearing it is what lets every slot re-roll against the source's odds on resubscribe. */
export function resetSequence(observable: {
  cursor: number;
  ticksSinceLastSpawn: number;
  sequence: MaterialId[];
  completed: boolean;
}): void {
  observable.cursor = 0;
  observable.ticksSinceLastSpawn = 0;
  observable.sequence.length = 0;
  observable.completed = false;
}

/** Unsubscribing tears down the whole pipeline — nothing already in flight gets to finish its trip. */
function discardAllPackets(state: SimState): SimEvent[] {
  const events: SimEvent[] = [];
  const despawn = (packetId: PacketId): void => {
    delete state.packets[packetId];
    events.push({ type: 'packetDespawned', packetId });
  };
  Object.values(state.conveyors).forEach((conveyor) => {
    if (conveyor.slot !== null) {
      despawn(conveyor.slot);
      conveyor.slot = null;
    }
  });
  Object.values(state.observables).forEach((observable) =>
    observable.output.queue.splice(0).forEach(despawn),
  );
  Object.values(state.machines).forEach((machine) =>
    releaseMachinePackets(machine).forEach(despawn),
  );
  Object.values(state.sinks).forEach((sink) => sink.input.queue.splice(0).forEach(despawn));
  return events;
}

/** Every observable replays from scratch on its next subscription, matching RxJS's per-subscription semantics — and every source is back to full ground for it to draw on. */
export function unsubscribeAll(state: SimState): void {
  state.subscribed = false;
  Object.values(state.observables).forEach(resetSequence);
  Object.values(state.subjects).forEach((subject) => (subject.fired = false));
  state.sources.forEach((source) => (source.extracted = 0));
}

/**
 * One shared switch stands in for a single downstream `.subscribe()` call across every observable.
 * Unsubscribing tears down the whole pipeline immediately, so anything still in flight is
 * discarded rather than left to finish its trip — unlike `unsubscribeAll` on its own, which `take`
 * and `takeUntil` call internally. Those two let the belt drain: `take`'s last packet is the one
 * that completed the count and must reach the subscriber rather than be swept away by its own
 * completion, and `takeUntil` ends the stream on something happening elsewhere entirely.
 */
export function toggleSubscribe(state: SimState): SimEvent[] {
  if (state.subscribed) return tearDown(state);
  state.subscribed = true;
  return [];
}

/** The packet that threw, already off whatever port it was taken from by the time it did. */
export interface SubscriptionFailure {
  packetId: PacketId;
  material: PacketMaterial;
  message: string;
}

/** Everything the game throws is an `Error`, but `catch` is untyped and reporting a failure must not
 * itself become one. */
export function thrownMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * A throw anywhere in the stream ends the subscription exactly the way `unsubscribe()` does: RxJS
 * delivers at most one terminal notification, and nothing flows after it.
 *
 * The `error` column changes none of that — it is terminal whether or not anything is listening. It
 * changes only whether the failure gets reported: the notification goes straight to the subscriber,
 * skipping everything downstream of wherever it was raised, and with no handler installed it has
 * nowhere to land, so the stream dies without saying why.
 */
export function errorSubscription(state: SimState, failure: SubscriptionFailure): SimEvent[] {
  const handler = state.subscriber?.functions.error[0];
  const report = handler ? AuditService[handler](failure.message) : null;
  delete state.packets[failure.packetId];
  return [
    { type: 'subscriptionErrored', ...failure, report },
    { type: 'packetDespawned', packetId: failure.packetId },
    ...tearDown(state),
  ];
}

function tearDown(state: SimState): SimEvent[] {
  unsubscribeAll(state);
  return discardAllPackets(state);
}
